"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";

import { useGetCategories } from "@/actions/category";

interface FooterLink {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
  extra?: React.ReactNode;
  width?: string;
}

const MAX_CATEGORY_LINKS = 5;

const SOLUTIONS_LINKS: FooterLink[] = [
  { label: "Catalogue de formations", href: "#" },
  { label: "Formations Inter Entreprise", href: "#" },
  // { label: "Commerce", href: "#" },
  { label: "Formations Intra Entreprise", href: "#" },
  { label: "Formations sur-mesure", href: "#" },
];

const CATEGORIES_SKELETON = (
  <div className="mt-4.5 flex flex-col gap-3" aria-hidden>
    {Array.from({ length: MAX_CATEGORY_LINKS }).map((_, i) => (
      <div
        key={i}
        className="h-3 w-full max-w-40 rounded bg-white/10 animate-pulse"
      />
    ))}
  </div>
);

const STATIC_FOOTER_LINKS: FooterSection[] = [
  {
    title: "Nous contacter",
    width: "143px",
    links: [
      { label: "Nous contacter", href: "/contact" },
      // { label: "A propos de nous", href: "/apropos" },
      // { label: "minimal@minimal.com", href: "mailto:minimal@minimal.com" },
      // { label: "Publier sa formation", href: "#" },
      // { label: "Devenir formateur", href: "#" },
    ],
  },
  {
    title: "Nous joindre",
    width: "143px",
    links: [],
    extra: (
      <div className="flex gap-4.5 justify-start mt-3">
        {[
          { icon: "instagram.svg", alt: "Instagram" },
          { icon: "facebook.svg", alt: "Facebook" },
          { icon: "youtube.svg", alt: "YouTube" },
        ].map((social) => (
          <Link
            key={social.alt}
            href="#"
            aria-label={`Visit our ${social.alt} page`}
            title={`Visit our ${social.alt} page`}
          >
            <Image
              src={`/assets/icons/${social.icon}`}
              alt={social.alt}
              title={social.alt}
              width={24}
              height={23}
            />
          </Link>
        ))}
      </div>
    ),
  },
];

export default function Footer() {
  const { categories, categoriesLoading, categoriesEmpty } = useGetCategories();

  let categoriesSection: FooterSection | null = null;

  if (categoriesLoading) {
    categoriesSection = {
      title: "Nos Formations par catégorie",
      width: "188px",
      links: [],
      extra: CATEGORIES_SKELETON,
    };
  } else if (!categoriesEmpty) {
    const links: FooterLink[] = categories
      .slice(0, MAX_CATEGORY_LINKS)
      .map((category) => ({
        label: category.name,
        href: `/formations/${category.slug}`,
      }));

    if (categories.length > MAX_CATEGORY_LINKS) {
      links.push({ label: "Voir toutes nos formations", href: "/formations" });
    }

    categoriesSection = {
      title: "Nos Formations par catégorie",
      width: "188px",
      links,
    };
  }

  const footerSections: FooterSection[] = categoriesSection
    ? [categoriesSection, ...STATIC_FOOTER_LINKS]
    : STATIC_FOOTER_LINKS;

  // data-print-hide: pages that expose an "Imprimer" action print their own
  // content, not the site-wide navigation. See hooks/usePrintPage.
  return (
    <div
      data-print-hide
      className="w-full bg-[#1B365E] xl:rounded-t-sm pt-10.5 xl:pt-8.25 pb-5 xl:pb-2.25 px-4 md:px-6 text-white"
    >
      <div className="w-full max-w-346.25 mx-auto">
        {/* Logo */}
        {/* chnaged the mb-6.5 TO pt-6.5 AND added balis div in line  */}
        {/* <div className="hidden xl:flex pt-6.5">
                    <Image
                        src="/assets/logo.svg"
                        alt="Spot Formations"
                        title="Spot Formations"
                        width={221}
                        height={15}
                    />
                </div> */}

        <div className="flex items-start xl:flex-row flex-col gap-8 lg:gap-11.5">
          {/* Left section with image and description */}
          <div className="w-full lg:max-w-134 flex items-start flex-col">
            {/* Image & Desc Column */}
            <div className="xl:pb-4 flex flex-col xl:flex-row items-center xl:items-start gap-12 xl:gap-6.5 justify-between w-full">
              {/* Logo (shown on all breakpoints, aligned top with heading on desktop) */}
              <div className="w-full xl:w-auto flex justify-start shrink-0 flex-col gap-6 mx-6.5">
                <Image
                  src="/assets/logo.svg"
                  alt="Spot Formations"
                  title="Spot Formations"
                  width={221}
                  height={15}
                />
                <p className="font-lato font-normal text-[12px] leading-5 tracking-[0.24px]">
                  Spot Formations n&apos;a qu&apos;une mission :<br /> vous
                  guider vers la reussite.
                </p>
                <Link
                  href="/organisme-de-formation"
                  className="font-lato font-normal text-[12px] leading-5 tracking-[0.24px] border border-[#40BFDE] rounded-lg px-4 py-2 text-center transition-colors duration-200 hover:bg-[#40BFDE] hover:text-white"
                >
                  Je suis un organisme de formations
                </Link>
                <Link
                  href="/enseigner"
                  className="font-lato font-normal text-[12px] leading-5 tracking-[0.24px] border border-[#40BFDE] rounded-lg px-4 py-2 text-center transition-colors duration-200 hover:bg-[#40BFDE] hover:text-white"
                >
                  Je suis un formateur
                </Link>
              </div>

              {/* Description Text Column */}
              <div className="w-full xl:max-w-71.25 space-y-2.75">
                <p className="font-lato font-bold text-[15px] leading-4.5 tracking-[0.3px]">
                  Toutes nos solutions
                </p>
                <div className="mt-4.5">
                  {SOLUTIONS_LINKS.map((link, linkIndex) => (
                    <div
                      key={linkIndex}
                      className="text-[12px] leading-5 font-lato font-normal tracking-[0.24px]"
                    >
                      <Link
                        href={link.href}
                        title={link.label}
                        className="text-white transition-colors duration-200 hover:text-[#40BFDE] wrap-break-word"
                      >
                        {link.label}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Language selector - Desktop Position (Hidden on Mobile) */}
            {/* <div className="hidden lg:flex items-center gap-2.5 text-white mt-5">
                            <Image
                                src="/assets/icons/globe.svg"
                                alt="Language Icon"
                                title="Language Icon"
                                width={24}
                                height={24}
                            />
                            <span className="font-lato font-normal text-[12px] leading-5 text-[#C7F8FF]">
                                Français (FR)
                            </span>
                        </div> */}
          </div>

          {/* Right section with columns */}
          <div
            className="flex-1 w-full xl:w-fit flex flex-col gap-8 md:gap-5 md:flex-row md:flex-wrap lg:flex-nowrap justify-between"
            aria-label="Footer Navigation"
          >
            {footerSections.map((section, index) => (
              <div
                key={index}
                className="flex flex-col w-full md:w-auto lg:w-(--col-width)"
                style={{ "--col-width": section.width } as React.CSSProperties}
              >
                <p className="md:h-9 font-lato font-bold text-[15px] leading-4.5 tracking-[0.3px] text-white">
                  {section.title}
                </p>
                <div className="mt-4.5 p-0 last:mb-0 mb-4.5 sm:mb-6">
                  {section.links.map((link, linkIndex) => (
                    <div
                      key={linkIndex}
                      className="text-[12px] leading-5 font-lato font-normal tracking-[0.24px]"
                    >
                      <Link
                        href={link.href}
                        title={link.label}
                        className="text-white transition-colors duration-200 hover:text-[#40BFDE] wrap-break-word"
                      >
                        {link.label}
                      </Link>
                    </div>
                  ))}
                </div>
                {section.extra}
              </div>
            ))}
          </div>
        </div>

        {/* Language selector - Mobile/Tablet (Visible only on mobile/tablet) */}
        {/* <div className="xl:hidden border-t border-[#C7F8FF] pt-8 flex items-center gap-2.5 text-white mt-8">
                    <Image
                        src="/assets/icons/globe.svg"
                        alt="Language Icon"
                        title="Language Icon"
                        width={24}
                        height={24}
                    />
                    <span className="font-lato font-normal text-[12px] leading-5 text-[#C7F8FF]">
                        Français (FR)
                    </span>
                </div> */}

        {/* Copyright */}
        <div className="flex justify-start sm:justify-center mt-8 xl:mt-10">
          <p className="font-lato font-normal text-[12px] leading-5 text-[#FFBC42]">
            Spot Formations {new Date().getFullYear()} © Tous les droits
            réservés
          </p>
        </div>
      </div>
    </div>
  );
}
